import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface FragItem { name: string; errorRate: number; total: number; }

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const FragilitySubjectsModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const [items, setItems] = useState<FragItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        fetch('/api/question-stats/fragility-index', {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (d?.fragility) {
                    const sorted = [...d.fragility].sort((a: FragItem, b: FragItem) => b.errorRate - a.errorRate);
                    setItems(sorted.slice(0, 10));
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.60)',
                backdropFilter: 'blur(4px)',
                zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#042563',
                    borderRadius: '16px',
                    padding: '24px 20px 20px',
                    maxWidth: '460px',
                    width: '100%',
                    position: 'relative',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.45)'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                    <AlertTriangle size={17} color="var(--alert-fragility, #F0997B)" />
                    <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#fff', flex: 1 }}>
                        Assuntos Frágeis
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.10)',
                            border: 'none', cursor: 'pointer',
                            color: 'rgba(255,255,255,0.65)',
                            padding: '5px', borderRadius: '7px',
                            display: 'flex', alignItems: 'center',
                        }}
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* Body */}
                {loading ? (
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', padding: '24px 0', margin: 0 }}>
                        Carregando...
                    </p>
                ) : items.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', padding: '24px 0', margin: 0 }}>
                        Responda questões para ver a análise de fragilidade.
                    </p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        {items.map((item, i) => (
                            <div
                                key={i}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    background: 'rgba(255,255,255,0.08)',
                                    borderRadius: '10px',
                                    padding: '9px 13px'
                                }}
                            >
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    minWidth: '18px',
                                    textAlign: 'center',
                                    color: i === 0 ? '#E8B84B' : 'rgba(255,255,255,0.35)'
                                }}>
                                    {i + 1}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#fff',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.name}
                                    </div>
                                    <div style={{
                                        fontSize: '11px',
                                        color: 'rgba(255,255,255,0.40)',
                                        marginTop: '2px'
                                    }}>
                                        {item.total} questão{item.total !== 1 ? 'ões' : ''} respondida{item.total !== 1 ? 's' : ''}
                                    </div>
                                </div>
                                <span style={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: 'var(--alert-fragility, #F0997B)',
                                    flexShrink: 0
                                }}>
                                    {item.errorRate}%
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FragilitySubjectsModal;
